import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Video, Camera, MapPin, Upload, ChevronDown, ChevronUp, CheckCircle, Clock, Bell, AlertCircle, Play } from 'lucide-react';
import api from '../services/api';

const FieldVerification = () => {
  const [assets, setAssets] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTalukas, setExpandedTalukas] = useState({
    'Salcette': true,
    'Sanguem': false
  });
  const [expandedVillages, setExpandedVillages] = useState({});
  
  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [notificationData, setNotificationData] = useState(null);
  
  // Form states
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('Verified');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [assetsRes, areasRes] = await Promise.all([
        api.assets.getAll(),
        api.areas.getAll()
      ]);
      
      setAssets(assetsRes.data);
      setAreas(areasRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const talukas = areas.filter(a => a.type === 'Taluka');
  const villages = areas.filter(a => a.type === 'Village');

  const getVillagesByTaluka = (talukaName) => {
    const taluka = talukas.find(t => t.name === talukaName);
    if (!taluka) return [];
    return villages.filter(v => v.parent_area_id === taluka.id);
  };

  const getAssetsByVillage = (villageId) => {
    // Get village details
    const village = areas.find(a => a.id === villageId);
    if (!village) return [];
    
    // Find assets by matching village name in asset name
    // Assets are named like "Margao Reservoir 1", "Vasco WTP 1", etc.
    return assets.filter(asset => {
      if (!asset.name) return false;
      // Match village name in asset name (case-insensitive)
      return asset.name.toLowerCase().includes(village.name.toLowerCase());
    });
  };

  const toggleTaluka = (name) => {
    setExpandedTalukas(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const toggleVillage = (villageId) => {
    setExpandedVillages(prev => ({
      ...prev,
      [villageId]: !prev[villageId]
    }));
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (50 MB = 50 * 1024 * 1024 bytes)
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('File size exceeds 50 MB limit. Please select a smaller file.');
        return;
      }
      
      // Check if it's a video file
      if (!file.type.startsWith('video/')) {
        alert('Please select a valid video file.');
        return;
      }
      
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
    }
  };

  const handleUploadVerification = async () => {
    if (!videoFile) {
      alert('Please select a video file.');
      return;
    }

    setUploading(true);
    
    try {
      // Simulate upload (in real app, this would upload to backend)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const timestamp = new Date().toISOString();
      const village = areas.find(a => a.id === selectedAsset.parent_area_id);
      const taluka = village && areas.find(a => a.id === village.parent_area_id);
      
      // Create notification data
      const notification = {
        asset: selectedAsset,
        village: village?.name || 'Unknown',
        taluka: taluka?.name || 'Unknown',
        videoFileName: videoFile.name,
        videoSize: (videoFile.size / (1024 * 1024)).toFixed(2) + ' MB',
        timestamp: timestamp,
        status: verificationStatus,
        notes: verificationNotes,
        verifiedBy: 'Field Officer - South Goa'
      };
      
      setNotificationData(notification);
      setShowUploadModal(false);
      setShowNotificationModal(true);
      
      // Reset form
      setVideoFile(null);
      setVideoPreview(null);
      setVerificationNotes('');
      setVerificationStatus('Verified');
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSendNotification = () => {
    alert('Verification notification sent to all stakeholders via Email & SMS!');
    setShowNotificationModal(false);
    setNotificationData(null);
  };

  const getAssetIcon = (type) => {
    switch(type) {
      case 'WTP': return <Camera size={16} className="text-purple-400" />;
      case 'Reservoir': return <MapPin size={16} className="text-blue-400" />;
      case 'OHT': return <Video size={16} className="text-green-400" />;
      case 'DMA': return <MapPin size={16} className="text-orange-400" />;
      default: return <MapPin size={16} className="text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading field verification data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="field-verification-module">
      {/* Header */}
      <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video size={20} />
            Field Verification - Video Upload & Asset Inspection
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-400 text-sm">
            Upload field verification videos for assets organized by Taluka and Village.
            Maximum video size: 50 MB. Videos are timestamped and stakeholders are notified immediately.
          </p>
        </CardContent>
      </Card>

      {/* Hierarchical Structure */}
      <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
        <CardHeader>
          <CardTitle>Assets by Location</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Salcette Taluka */}
            <div className="border border-slate-700 rounded-lg">
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-800 transition-colors"
                onClick={() => toggleTaluka('Salcette')}
              >
                <div className="flex items-center gap-3">
                  <MapPin size={18} className="text-blue-400" />
                  <span className="font-semibold text-lg">Salcette Taluka</span>
                  <span className="text-sm text-gray-400">
                    ({getVillagesByTaluka('Salcette').length} villages)
                  </span>
                </div>
                {expandedTalukas['Salcette'] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </div>
              
              {expandedTalukas['Salcette'] && (
                <div className="p-4 space-y-3 bg-slate-800/50">
                  {getVillagesByTaluka('Salcette').map(village => {
                    const villageAssets = getAssetsByVillage(village.id);
                    return (
                      <div key={village.id} className="border border-slate-600 rounded-lg">
                        <div 
                          className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-700 transition-colors"
                          onClick={() => toggleVillage(village.id)}
                        >
                          <div className="flex items-center gap-2">
                            <MapPin size={16} className="text-green-400" />
                            <span className="font-medium">{village.name}</span>
                            <span className="text-xs text-gray-400">
                              ({villageAssets.length} assets)
                            </span>
                          </div>
                          {expandedVillages[village.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </div>
                        
                        {expandedVillages[village.id] && (
                          <div className="p-3 space-y-2 bg-slate-700/50">
                            {villageAssets.length === 0 ? (
                              <p className="text-sm text-gray-400 text-center py-2">No assets in this village</p>
                            ) : (
                              villageAssets.map(asset => (
                                <div 
                                  key={asset.id} 
                                  className="flex items-center justify-between p-3 bg-slate-800 rounded-lg border border-slate-600 hover:border-blue-500 transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    {getAssetIcon(asset.type)}
                                    <div>
                                      <p className="font-medium text-sm">{asset.name}</p>
                                      <p className="text-xs text-gray-400">{asset.type}</p>
                                    </div>
                                  </div>
                                  <Button 
                                    size="sm"
                                    onClick={() => {
                                      setSelectedAsset(asset);
                                      setShowUploadModal(true);
                                    }}
                                    className="bg-blue-600 text-white border-0 hover:bg-blue-700"
                                  >
                                    <Upload size={14} className="mr-1" />
                                    Upload Video
                                  </Button>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Sanguem Taluka */}
            <div className="border border-slate-700 rounded-lg">
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-800 transition-colors"
                onClick={() => toggleTaluka('Sanguem')}
              >
                <div className="flex items-center gap-3">
                  <MapPin size={18} className="text-green-400" />
                  <span className="font-semibold text-lg">Sanguem Taluka</span>
                  <span className="text-sm text-gray-400">
                    ({getVillagesByTaluka('Sanguem').length} villages)
                  </span>
                </div>
                {expandedTalukas['Sanguem'] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </div>
              
              {expandedTalukas['Sanguem'] && (
                <div className="p-4 space-y-3 bg-slate-800/50">
                  {getVillagesByTaluka('Sanguem').map(village => {
                    const villageAssets = getAssetsByVillage(village.id);
                    return (
                      <div key={village.id} className="border border-slate-600 rounded-lg">
                        <div 
                          className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-700 transition-colors"
                          onClick={() => toggleVillage(village.id)}
                        >
                          <div className="flex items-center gap-2">
                            <MapPin size={16} className="text-green-400" />
                            <span className="font-medium">{village.name}</span>
                            <span className="text-xs text-gray-400">
                              ({villageAssets.length} assets)
                            </span>
                          </div>
                          {expandedVillages[village.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </div>
                        
                        {expandedVillages[village.id] && (
                          <div className="p-3 space-y-2 bg-slate-700/50">
                            {villageAssets.length === 0 ? (
                              <p className="text-sm text-gray-400 text-center py-2">No assets in this village</p>
                            ) : (
                              villageAssets.map(asset => (
                                <div 
                                  key={asset.id} 
                                  className="flex items-center justify-between p-3 bg-slate-800 rounded-lg border border-slate-600 hover:border-blue-500 transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    {getAssetIcon(asset.type)}
                                    <div>
                                      <p className="font-medium text-sm">{asset.name}</p>
                                      <p className="text-xs text-gray-400">{asset.type}</p>
                                    </div>
                                  </div>
                                  <Button 
                                    size="sm"
                                    onClick={() => {
                                      setSelectedAsset(asset);
                                      setShowUploadModal(true);
                                    }}
                                    className="bg-blue-600 text-white border-0 hover:bg-blue-700"
                                  >
                                    <Upload size={14} className="mr-1" />
                                    Upload Video
                                  </Button>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Video Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video size={20} />
              Upload Field Verification Video
            </DialogTitle>
            <DialogDescription>
              Upload verification video for {selectedAsset?.name} (Max 50 MB)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Asset Info */}
            <div className="p-3 bg-slate-100 rounded-lg border border-slate-300">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="font-semibold text-gray-700">Asset:</span>
                  <span className="text-gray-900 ml-2">{selectedAsset?.name}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Type:</span>
                  <span className="text-gray-900 ml-2">{selectedAsset?.type}</span>
                </div>
              </div>
            </div>

            {/* Video Upload */}
            <div className="space-y-2">
              <Label>Select Video File *</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoSelect}
                  className="flex-1"
                />
                {videoFile && (
                  <span className="text-sm text-gray-600">
                    {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500">Maximum file size: 50 MB</p>
            </div>

            {/* Video Preview */}
            {videoPreview && (
              <div className="space-y-2">
                <Label>Video Preview</Label>
                <div className="aspect-video bg-black rounded-lg overflow-hidden">
                  <video 
                    src={videoPreview} 
                    controls 
                    className="w-full h-full"
                  >
                    Your browser does not support video playback.
                  </video>
                </div>
              </div>
            )}

            {/* Verification Status */}
            <div className="space-y-2">
              <Label>Verification Status *</Label>
              <select
                value={verificationStatus}
                onChange={(e) => setVerificationStatus(e.target.value)}
                className="w-full bg-white border-slate-300 text-gray-900 rounded px-3 py-2"
              >
                <option value="Verified">✓ Verified - No Issues</option>
                <option value="Needs Attention">⚠ Needs Attention</option>
                <option value="Critical Issue">❌ Critical Issue Found</option>
                <option value="Under Maintenance">🔧 Under Maintenance</option>
              </select>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Verification Notes</Label>
              <Textarea
                value={verificationNotes}
                onChange={(e) => setVerificationNotes(e.target.value)}
                placeholder="Add notes about the verification (optional)..."
                rows={3}
              />
            </div>

            {/* Timestamp Display */}
            <div className="space-y-2">
              <Label>Verification Timestamp</Label>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-blue-600" />
                  <span className="text-sm font-mono text-gray-700">
                    {new Date().toLocaleString('en-IN', { 
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: false
                    })}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Video will be timestamped at upload time
                </p>
              </div>
            </div>

            {/* Timestamp Info */}
            <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 p-2 rounded">
              <Clock size={16} />
              <span>Video will be timestamped: {new Date().toLocaleString()}</span>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowUploadModal(false);
                setVideoFile(null);
                setVideoPreview(null);
              }}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUploadVerification} 
              className="bg-gradient-to-br from-cyan-600/80 to-cyan-700/80 hover:from-cyan-600 hover:to-cyan-700 border border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/30"
              disabled={uploading || !videoFile}
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={14} className="mr-1" />
                  Upload & Notify
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification Modal */}
      <Dialog open={showNotificationModal} onOpenChange={setShowNotificationModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle size={24} />
              Video Uploaded Successfully
            </DialogTitle>
            <DialogDescription>
              Field verification completed. Notify stakeholders now?
            </DialogDescription>
          </DialogHeader>
          {notificationData && (
            <div className="space-y-4 py-4">
              {/* Verification Summary */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-semibold text-green-900 mb-3">Verification Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-green-800">Location:</span>
                    <span className="text-green-700">{notificationData.taluka} → {notificationData.village}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-green-800">Asset:</span>
                    <span className="text-green-700">{notificationData.asset.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-green-800">Status:</span>
                    <span className="text-green-700">{notificationData.status}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-green-800">Video File:</span>
                    <span className="text-green-700">{notificationData.videoFileName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-green-800">File Size:</span>
                    <span className="text-green-700">{notificationData.videoSize}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-green-800">Timestamp:</span>
                    <span className="text-green-700">{new Date(notificationData.timestamp).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-green-800">Verified By:</span>
                    <span className="text-green-700">{notificationData.verifiedBy}</span>
                  </div>
                  {notificationData.notes && (
                    <div className="pt-2 border-t border-green-300">
                      <span className="font-medium text-green-800">Notes:</span>
                      <p className="text-green-700 mt-1">{notificationData.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Stakeholders to Notify */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Bell size={18} />
                  Stakeholders to be Notified:
                </h4>
                <div className="space-y-2">
                  <div className="p-3 bg-slate-100 rounded-lg border border-slate-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">Department Head - Field Operations</p>
                        <p className="text-sm text-gray-600">fieldops.head@goa.gov.in</p>
                        <p className="text-sm text-gray-600">+91-832-2712066</p>
                      </div>
                      <CheckCircle className="text-green-600" size={20} />
                    </div>
                  </div>
                  <div className="p-3 bg-slate-100 rounded-lg border border-slate-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">Technical Manager - {notificationData.taluka}</p>
                        <p className="text-sm text-gray-600">tech.{notificationData.taluka.toLowerCase()}@goa.gov.in</p>
                        <p className="text-sm text-gray-600">+91-9822-XXXXXX</p>
                      </div>
                      <CheckCircle className="text-green-600" size={20} />
                    </div>
                  </div>
                  <div className="p-3 bg-slate-100 rounded-lg border border-slate-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">District Collector - South Goa</p>
                        <p className="text-sm text-gray-600">dc.southgoa@goa.gov.in</p>
                        <p className="text-sm text-gray-600">+91-832-2712066</p>
                      </div>
                      <CheckCircle className="text-green-600" size={20} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Notification Message Preview */}
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-900">Notification Message:</h4>
                <div className="p-4 bg-gray-50 border border-gray-300 rounded-lg">
                  <p className="text-sm text-gray-800 whitespace-pre-line">
                    📹 <strong>Field Verification Completed</strong>
                    
                    <strong>Location:</strong> {notificationData.taluka} Taluka → {notificationData.village}
                    <strong>Asset:</strong> {notificationData.asset.name} ({notificationData.asset.type})
                    <strong>Status:</strong> {notificationData.status}
                    <strong>Video:</strong> {notificationData.videoFileName} ({notificationData.videoSize})
                    <strong>Timestamp:</strong> {new Date(notificationData.timestamp).toLocaleString()}
                    <strong>Verified By:</strong> {notificationData.verifiedBy}
                    {notificationData.notes && `\n<strong>Notes:</strong> ${notificationData.notes}`}
                    
                    Video available for review in the system.
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotificationModal(false)}>
              Close
            </Button>
            <Button 
              onClick={handleSendNotification}
              className="bg-green-600 hover:bg-green-700"
            >
              <Bell size={14} className="mr-1" />
              Send Notification Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FieldVerification;
